import { signal } from "@preact/signals-react";
import { getIt } from "@uikit/getIt";
import { BrainClient } from "imagica-corekit/dist/base/api/BrainClient";
import {
  Message,
  PromptModel,
  PromptModelResponse,
} from "imagica-corekit/dist/base/api/chatCompletionTyped/ChatCompletionsTyped";

import { tryPromise } from "imagica-corekit/dist/base/cutil/LangUtil";
import { StreamReadResult } from "imagica-corekit/dist/base/cutil/StreamClient";
import { findLastIndex } from "lodash";

export enum Role {
  ASSISTANT = "assistant",
  USER = "user",
}

export class IntelligentPrompt {
  static customJSFunctionChat = {
    domain: "brain_studios",
    model_params: "custom_js_function_chat_agent.model",
  };
  static genUI = {
    domain: "GenUI_experiment",
    model_params: "genUI1.0.model",
  };
}

class ChatState {
  promptInfo = signal<PromptModelResponse | undefined>(undefined);
  prompt = signal<string>("");
  message = signal<Message[]>([]);
}

export class ChatBloc {
  constructor(public promptModel: PromptModel) {}
  state = new ChatState();
  brainClient = getIt(BrainClient);

  transformPromptModel(): PromptModel {
    const domain = this.promptModel.domain;
    const model_params = `${domain}_xxx_${this.promptModel.model_params}`;

    return {
      domain,
      model_params,
    };
  }

  transformGPTPrompt(): void {
    const systemRegex = /---system---([\s\S]*?)---system---/;
    const systemMatch = this.state.promptInfo.value?.prompt?.match(systemRegex);

    if (systemMatch) {
      this.state.prompt.value = systemMatch[1];
    } else {
      console.error("Can not match");
    }
  }

  structureMessage(): Message[] {
    const message = [
      {
        content: this.state.prompt.value,
        role: "system",
      },
      ...this.state.message.value,
    ];

    return message;
  }

  updateMessage(latestMsg: string, role: Role): void {
    this.state.message.value = [
      ...this.state.message.value,
      {
        content: latestMsg,
        role,
      },
    ];
  }

  addOrUpdateAssistantMsg(msg: string): void {
    const index = findLastIndex(this.state.message.value);
    if (this.state.message.value[index]?.role === Role.USER) {
      return this.updateMessage(msg, Role.ASSISTANT);
    }
    const newMsg = [...this.state.message.value];
    newMsg[index].content = msg;
    this.state.message.value = newMsg;
  }

  clearMessage(): void {
    this.state.message.value = [];
  }

  async getPromptInfoByAPI(): Promise<void> {
    const result = await tryPromise(this.brainClient.openAi.instantIntent(this.transformPromptModel()));
    this.state.promptInfo.value = result.data?.data;
    this.transformGPTPrompt();
  }

  async getNewChatInfoByAPI(useInput: string): Promise<void> {
    this.updateMessage(useInput, Role.USER);
    await new Promise(resolve => {
      let onReceived = (msg: StreamReadResult) => {
        if (msg.done) {
          msg.abort();
          resolve(undefined);
        }
        const fullText = msg.fullText.trim();
        this.addOrUpdateAssistantMsg(fullText);
      };

      this.brainClient.openAi
        .chatCompletions({
          messages: this.structureMessage(),
          model: this.state.promptInfo.value?.engine!,
          stream: true,
        })
        .then(re => {
          re.onReceived(re => onReceived(re));
        })
        .catch(error => {
          console.error(error);
        });
    });
  }
}
